import { createFileRoute, redirect } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { firstAllowedRoute, hasModule } from "@/lib/auth";
import { ConfirmationsClient } from "@/features/warehouse/ConfirmationsClient";
import { CONFIRMATION_VIEWS, confirmationKeys, listConfirmations, type ConfirmationView } from "@/features/warehouse/confirmations.api";

// Warehouse → Bevestigingen (docs/wms-bevestigingen.md): the inbound and outbound confirmations to customers. Reading
// for whoever reads warehouse or transport orders; approving and sending without the freight document for whoever
// writes them — the back-office (Camiel, 20-10-2026). The API checks it regardless.

const READ = ["wms_inbound", "wms_outbound", "tms_orders", "wms_inbound_write", "wms_outbound_write", "tms_orders_write"] as const;
const WRITE = ["wms_inbound_write", "wms_outbound_write", "tms_orders_write"] as const;

interface Search {
  view?: ConfirmationView;
  q?: string;
}

export const Route = createFileRoute("/_shell/warehouse/confirmations")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    ...(CONFIRMATION_VIEWS.includes(search["view"] as ConfirmationView) ? { view: search["view"] as ConfirmationView } : {}),
    ...(typeof search["q"] === "string" && search["q"] ? { q: search["q"] } : {}),
  }),
  beforeLoad: ({ context }) => {
    if (!READ.some((key) => hasModule(context.session, key))) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  component: ConfirmationsScreen,
});

function ConfirmationsScreen() {
  const { session } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const view = search.view ?? "open";
  const q = search.q ?? "";
  const query = useQuery({ queryKey: confirmationKeys.list(view, q), queryFn: () => listConfirmations(view, q), placeholderData: keepPreviousData });
  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Warehouse Confirmations">
      <ConfirmationsClient
        rows={query.data?.confirmations ?? []}
        isLoading={query.isLoading}
        error={query.error}
        userId={session.userId}
        canEdit={WRITE.some((key) => hasModule(session, key))}
        view={view}
        q={q}
        onView={(next) => void navigate({ search: (prev) => ({ ...prev, view: next === "open" ? undefined : next }) })}
        onSearch={(next) => void navigate({ search: (prev) => ({ ...prev, q: next || undefined }) })}
      />
    </div>
  );
}
